import type {
    AddCommerceCartLineRequest,
    CommerceCart,
    CommerceCartChangeReason,
    CommerceCartChangedEvent,
    CommerceCartChangedListener,
    CommerceCartDomain,
    CommerceEnrollmentCart,
    CommerceEventUnsubscribe,
    CommerceOperationOptions,
    UpdateCommerceCartLineQuantityRequest,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceCartRepository } from "./commerce-cart-repository.js";
import {
    normalizeAddCartLineRequest,
    normalizeUpdateQuantityRequest,
    requireCouponCode,
    requireIdentifier,
} from "./cart-request-normalizer.js";

export class CommerceCartService implements CommerceCartDomain {
    private readonly listeners = new DisposableListenerRegistry<CommerceCartChangedEvent>();
    private currentCart: CommerceCart | undefined;
    private synchronizeMutation: ((cart: CommerceCart) => Promise<void>) | undefined;
    private mutationTail: Promise<void> = Promise.resolve();

    public constructor(private readonly repository: CommerceCartRepository) {}

    public get current(): CommerceCart | undefined {
        return this.currentCart;
    }

    public async get(options: CommerceOperationOptions = {}): Promise<CommerceCart> {
        await this.mutationTail;
        return this.accept(await this.repository.get(options), "loaded");
    }
    public async add(
        request: AddCommerceCartLineRequest,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCart> {
        return this.enqueueMutation(async () =>
            this.acceptMutation(
                await this.repository.add(
                    normalizeAddCartLineRequest(request, this.currentCart?.version),
                    options,
                ),
                "line-added",
            ),
        );
    }
    public async updateQuantity(
        request: UpdateCommerceCartLineQuantityRequest,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCart> {
        return this.enqueueMutation(async () =>
            this.acceptMutation(
                await this.repository.updateQuantity(
                    normalizeUpdateQuantityRequest(request, this.currentCart?.version),
                    options,
                ),
                "line-updated",
            ),
        );
    }
    public async remove(
        lineId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCart> {
        return this.enqueueMutation(async () =>
            this.acceptMutation(
                await this.repository.remove(
                    requireIdentifier(lineId, "Cart line ID"),
                    this.currentCart?.version,
                    options,
                ),
                "line-removed",
            ),
        );
    }
    public async clear(options: CommerceOperationOptions = {}): Promise<CommerceCart> {
        return this.enqueueMutation(async () =>
            this.acceptMutation(
                await this.repository.clear(this.currentCart?.version, options),
                "cleared",
            ),
        );
    }
    public async applyCoupon(
        code: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCart> {
        return this.enqueueMutation(async () =>
            this.acceptMutation(
                await this.repository.applyCoupon(
                    requireCouponCode(code),
                    this.currentCart?.version,
                    options,
                ),
                "coupon-applied",
            ),
        );
    }
    public async removeCoupon(
        code: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceCart> {
        return this.enqueueMutation(async () =>
            this.acceptMutation(
                await this.repository.removeCoupon(
                    requireCouponCode(code),
                    this.currentCart?.version,
                    options,
                ),
                "coupon-removed",
            ),
        );
    }
    public adopt(cart: CommerceCart): CommerceCart {
        return this.accept(cart, "share-accepted");
    }
    public adoptEnrollment(cart: CommerceEnrollmentCart, updatedAt: Date): CommerceCart {
        return this.accept(
            Object.freeze({
                id: cart.id,
                version: cart.version,
                currencyCode: cart.currencyCode,
                lines: Object.freeze(cart.lines.map((value) => value.line)),
                coupons: Object.freeze([]),
                totals: cart.totals,
                updatedAt,
            }),
            "enrollment-synchronized",
        );
    }
    public async adoptCheckout(cart: CommerceCart): Promise<CommerceCart> {
        const accepted =
            this.currentCart === cart ? cart : this.accept(cart, "checkout-synchronized");
        await this.synchronizeMutation?.(accepted);
        return accepted;
    }
    public adoptCheckoutSnapshot(cart: CommerceCart): CommerceCart {
        return this.accept(cart, "checkout-synchronized");
    }
    public onChanged(listener: CommerceCartChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }
    public dispose(): void {
        this.currentCart = undefined;
        this.listeners.release();
    }
    public invalidate(): void {
        this.currentCart = undefined;
    }

    public setMutationSynchronizer(synchronize: (cart: CommerceCart) => Promise<void>): void {
        this.synchronizeMutation = synchronize;
    }

    /**
     * Runs a Checkout mutation in the same sequence as Cart mutations.
     *
     * Checkout operations can also change Cart contents. Sharing this boundary prevents a
     * Checkout action from capturing an optimistic version while a Cart Toolbox mutation is
     * still being committed.
     */
    public coordinateMutation<T>(operation: () => Promise<T>): Promise<T> {
        return this.enqueueMutation(operation);
    }

    private enqueueMutation<T>(operation: () => Promise<T>): Promise<T> {
        const result = this.mutationTail.then(operation, operation);
        this.mutationTail = result.then(
            () => undefined,
            () => undefined,
        );
        return result;
    }

    private async acceptMutation(
        cart: CommerceCart,
        reason: CommerceCartChangeReason,
    ): Promise<CommerceCart> {
        const accepted = this.accept(cart, reason);
        await this.synchronizeMutation?.(accepted);
        return accepted;
    }

    private accept(cart: CommerceCart, reason: CommerceCartChangeReason): CommerceCart {
        this.currentCart = cart;
        this.listeners.emit(Object.freeze({ reason, cart }));
        return cart;
    }
}
